import { SetIOStep } from '@sb/remote-control/step/setIO';
import type { Robot } from '@sb/remote-control/types';

import type { Diagnostic, ValidationRule } from '../../types';
import { createStepRule, diag } from '../../util/ruleHelpers';

/**
 * Shape of an action's controlBoxIO outputs — duck-typed to avoid importing
 * from integration implementation internals.
 */
type ActionOutputsLike = {
  kind: string;
  outputs: Array<{ ioPort?: string | null }>;
};

type ActionLike = {
  id?: string;
  actionOutputs?: ActionOutputsLike;
};

/**
 * Check whether any of the action's controlBoxIO outputs target a port
 * assigned as safety IO. Returns a diagnostic if so.
 *
 * This replicates the old `validateSafetyOutputUsage` check that existed in
 * OperateEquipment, CNCOperateAutodoor, and CNCOperateWorkholding validators.
 */
function checkSafetyOutputUsage(
  ioOutputs: Robot.IOPort[],
  action: ActionLike,
  stepId: string,
  sourceRule: string,
): Diagnostic | null {
  const { actionOutputs } = action;

  if (actionOutputs == null || actionOutputs.kind !== 'controlBoxIO') {
    return null;
  }

  const usedOutputs = ioOutputs.filter((output) => {
    if (
      output.kind !== 'Output' &&
      output.kind !== 'Relay' &&
      output.kind !== 'FlangeOutput'
    ) {
      return false;
    }

    return actionOutputs.outputs.some(
      (actionOutput) =>
        actionOutput.ioPort ===
        SetIOStep.getPortLabel({
          kind: output.kind,
          port: output.port,
          level: 'low',
        }),
    );
  });

  const usedSafetyIOs = usedOutputs.filter(
    (output) => output.assignedTo?.kind === 'safetyIO',
  );

  if (usedSafetyIOs.length > 0) {
    return diag({
      code: 'ui.invalidConfiguration',
      message: 'Safety I/O outputs cannot be set using actions',
      fieldId: 'outputs',
      stepId,
      sourceRule,
    });
  }

  return null;
}

/**
 * Resolve the action object for CNC steps from the equipment config.
 * Returns null if the action cannot be resolved (device/action missing —
 * those errors are caught by the step-specific rules).
 */
function resolveCNCAction(
  equipment: Array<{
    id: string;
    config: { kind: string; [k: string]: unknown };
  }>,
  deviceID: string,
  actionGroup: string,
  actionID: string,
  workholdingID?: number,
): ActionLike | null {
  const device = equipment.find((e) => e.id === deviceID);

  if (!device || device.config.kind !== 'CNCMachine') return null;

  let group: { actions?: ActionLike[] } | undefined;

  if (actionGroup === 'autodoor') {
    group = device.config.autodoor as { actions?: ActionLike[] } | undefined;
  } else if (actionGroup === 'workholding' && workholdingID != null) {
    const workholdings = device.config.workholdings as
      | Array<{ actions?: ActionLike[] }>
      | undefined;

    group = workholdings?.[workholdingID];
  }

  if (!group?.actions) return null;

  return group.actions.find((a) => a.id === actionID) ?? null;
}

export const SafetyIOOutputsValidation: ValidationRule = createStepRule({
  id: 'steps.SafetyIOOutputs',
  stepKinds: [
    'OperateEquipment',
    'CNCOperateAutodoor',
    'CNCOperateWorkholding',
  ],
  run: ({ routine, ctx, stepId }) => {
    const { ioOutputs } = ctx;

    if (!ioOutputs || ioOutputs.length === 0) return [];

    const stepCfg = routine.stepConfigurations[stepId];
    if (!stepCfg?.args) return [];

    const args = stepCfg.args as Record<string, unknown>;

    const equipment = (ctx.equipment ?? []) as Array<{
      id: string;
      config: { kind: string; [k: string]: unknown };
    }>;

    let action: ActionLike | null = null;

    if (args.argumentKind === 'OperateEquipment') {
      // OperateEquipment stores action directly in step args
      action = (args.action as ActionLike) ?? null;
    } else if (args.argumentKind === 'CNCOperateAutodoor') {
      const deviceID = args.deviceID as string;
      const actionID = args.actionID as string;

      if (deviceID && actionID) {
        action = resolveCNCAction(equipment, deviceID, 'autodoor', actionID);
      }
    } else if (args.argumentKind === 'CNCOperateWorkholding') {
      const deviceID = args.deviceID as string;
      const workholdingID = args.workholdingID as number | undefined;
      const actionID = args.actionID as string;

      if (deviceID && actionID && workholdingID != null) {
        action = resolveCNCAction(
          equipment,
          deviceID,
          'workholding',
          actionID,
          workholdingID,
        );
      }
    }

    if (!action) return [];

    const result = checkSafetyOutputUsage(
      ioOutputs,
      action,
      stepId,
      'steps.SafetyIOOutputs',
    );

    return result ? [result] : [];
  },
});
